import { IDEA_VIDEO_TYPES, VIDEO_STYLES, CAPTION_TYPES } from './constants';
const fallback = (value, alt) => (value && value.trim() ? value.trim() : alt);
const seeds = {
    emotions: ['ấm áp', 'sâu lắng', 'thành thật', 'gãy gọn', 'gần gũi', 'động lực', 'cô đơn', 'hy vọng'],
    hooks: [
        'Có những ngày không ai hiểu mình, kể cả chính mình.',
        'Nếu bạn đang đi chậm, đoạn này là dành cho bạn.',
        'Không cần giỏi ngay từ đầu, chỉ cần đừng bỏ cuộc hôm nay.',
        'Một cảnh quay đẹp chưa chắc giữ người xem, nhưng một cảm xúc thật thì có.',
        'Thứ khiến video này chạm tới người xem không phải kỹ xảo, mà là sự thật.'
    ],
    scenes: [
        'đường phố đêm có mưa nhẹ',
        'góc bàn học cuối chiều',
        'hành lang trường học trống',
        'cửa sổ xe buýt nhìn ra thành phố',
        'ban công lúc hoàng hôn',
        'con đường nhỏ với ánh đèn vàng'
    ],
    colorVibes: ['xanh đêm', 'ấm vàng', 'đen trắng', 'hồng tím neon', 'cam hoàng hôn'],
    camera: ['cận mặt chậm', 'lia ngang nhẹ', 'góc thấp cinematic', 'POV bước đi', 'zoom chậm vào chủ thể']
};
export function buildIdeas(payload) {
    const topic = fallback(payload.topic, 'sự trưởng thành');
    const niche = fallback(payload.niche, 'content cảm xúc');
    const style = fallback(payload.style, 'tự sự điện ảnh');
    const goal = fallback(payload.goal, 'giữ người xem và tăng follow');
    const emotion = fallback(payload.emotion, 'sâu lắng');
    const selectedType = fallback(payload.videoType, IDEA_VIDEO_TYPES[0]);
    return Array.from({ length: 10 }).map((_, index) => {
        const hook = seeds.hooks[index % seeds.hooks.length];
        const scene = seeds.scenes[index % seeds.scenes.length];
        const mood = seeds.emotions[index % seeds.emotions.length];
        const score = 72 + ((index * 3) % 25);
        return {
            title: `${topic} #${index + 1}: ${['đánh thức', 'giữ lại', 'chạm vào', 'nói hộ', 'nhắc nhớ'][index % 5]} cảm xúc người xem`,
            description: `Một idea theo niche "${niche}", phong cách "${style}", tập trung mục tiêu ${goal}. Nội dung khai thác cảm xúc ${emotion} bằng bối cảnh ${scene}.`,
            hook,
            video_type: selectedType,
            emotion: mood,
            potential_score: score,
            scene_suggestion: `Cảnh chính: ${scene}; cảnh phụ: ${seeds.scenes[(index + 2) % seeds.scenes.length]}; nhịp cắt chậm, giữ 2-3 giây mỗi cảnh.`,
            caption_suggestion: `Có những đoạn trưởng thành không ồn ào, chỉ là mình không còn như trước nữa.`,
            hashtag_suggestion: `#tamtrang #${normalizeTag(topic)} #${normalizeTag(niche)} #xuhuong #fyp`,
            is_starred: index === 0 ? 1 : 0,
            tags: `${niche},${style},${emotion}`,
            status: 'da_luu'
        };
    });
}
export function buildScript(payload) {
    const title = fallback(payload.title, 'Video kể chuyện ngắn');
    const topic = fallback(payload.topic, title);
    const tone = fallback(payload.tone, 'sâu lắng');
    const duration = fallback(payload.duration, '20-30 giây');
    return {
        title,
        hook_opening: `${seeds.hooks[1]} — và có lẽ bạn cũng đã từng như vậy.`,
        voice_script: `Mình từng nghĩ cứ cố gắng thật nhiều là sẽ ổn. Nhưng rồi có những ngày, thứ mình cần nhất chỉ là một khoảng thở. ${topic} không phải để than vãn, mà để nhắc rằng ai cũng có quyền yếu lòng một chút rồi đứng dậy.`,
        overlay_text: `Không sao nếu hôm nay bạn mệt.\nChỉ cần đừng bỏ rơi bản thân.`,
        main_caption: `Có những lúc chậm lại không phải thua cuộc, mà là để trái tim kịp theo mình.`,
        hashtags: `#tamtrang #storytelling #${normalizeTag(topic)} #fyp #xh`,
        cta: `Nếu đoạn này nói đúng cảm xúc của bạn, hãy lưu lại để đọc khi cần.`,
        rhythm: 'Mở nhanh 2 giây, thân bài chậm và giữ nhạc nền đều, kết thúc bằng CTA 3 giây.',
        duration_hint: duration,
        voice_tone: tone,
        status: 'da_luu'
    };
}
export function buildCaptions(payload) {
    const topic = fallback(payload.topic, 'trưởng thành');
    const channel = fallback(payload.channelName, 'kênh tâm trạng');
    return CAPTION_TYPES.map((captionType, index) => ({
        caption_type: captionType,
        content: [
            `Có những ngày im lặng lại là cách mình tự cứu lấy mình. #${normalizeTag(topic)}`,
            `Mình kể câu chuyện này không phải để buồn, mà để ai đó thấy mình không cô đơn.`,
            `Không cần hơn ai cả. Hôm nay chỉ cần tốt hơn hôm qua là đủ.`,
            `Có phải càng lớn, chúng ta càng khó nói thật lòng không?`,
            `Một chút nhạc, một chút gió, một chút dịu dàng cho ${channel}.`,
            `Nếu bạn cũng từng đi qua giai đoạn này, hãy để lại một dấu chấm nhé.`,
            `Lưu video này lại, biết đâu vài ngày nữa bạn sẽ cần đúng câu này.`
        ][index],
        status: 'da_luu'
    }));
}
export function buildHashtags(payload) {
    const niche = fallback(payload.niche, 'tam trang');
    const emotion = fallback(payload.emotion, 'chill');
    const goal = fallback(payload.goal, 'keo view');
    return Array.from({ length: 4 }).map((_, index) => ({
        title: `Bộ hashtag ${index + 1}`,
        content: `#${normalizeTag(niche)} #${normalizeTag(emotion)} #${normalizeTag(goal)} #xuhuong #fyp #viral #tiktokvn`,
        target_goal: goal,
        status: 'da_luu'
    }));
}
export function buildPrompt(payload) {
    const base = fallback(payload.base, 'một khung cảnh phố đêm có cảm xúc');
    const style = fallback(payload.style, VIDEO_STYLES[0]);
    const detail = fallback(payload.detailLevel, 'cao');
    const targetDuration = fallback(payload.targetDuration, '20 giây');
    const colorVibe = fallback(payload.colorVibe, seeds.colorVibes[0]);
    const cameraHint = fallback(payload.cameraHint, seeds.camera[0]);
    return {
        title: `Prompt ${style}`,
        style,
        detail_level: detail,
        target_duration: targetDuration,
        color_vibe: colorVibe,
        camera_hint: cameraHint,
        content: `Tạo video dọc 9:16, phong cách ${style}, bối cảnh ${base}, ánh sáng ${colorVibe}, chuyển động máy ${cameraHint}, chi tiết ${detail}, tốc độ chậm vừa, cảm xúc điện ảnh, có chiều sâu, giữ chủ thể rõ ràng, phù hợp TikTok, ưu tiên khung hình dễ đọc subtitle, thời lượng mục tiêu ${targetDuration}.`,
        status: 'da_luu'
    };
}
export function buildChatReply(message, context) {
    const lower = message.toLowerCase();
    const channelName = context?.channelName || 'kênh hiện tại';
    if (lower.includes('10 idea')) {
        return `Cho ${channelName}, bạn nên đi theo 3 cụm nội dung: cảm xúc thật, POV gần gũi, và mini-story 15-25 giây. Hôm nay tôi gợi ý 10 idea xoay quanh “nói ít nhưng chạm sâu”, “đi qua một ngày tệ”, “người lớn không còn khóc to”, “đi học một mình”, “đường về nhà lúc mưa”.`;
    }
    if (lower.includes('caption')) {
        return `Caption hiệu quả cho ${channelName} nên ngắn, có nhịp ngắt và để người xem thấy mình trong đó. Mẫu gợi ý: “Không phải ngày nào cũng ổn. Nhưng mình vẫn đang cố.”`;
    }
    if (lower.includes('hook')) {
        return `Hook nên vào thẳng cảm xúc ngay 1-2 giây đầu: “Có ai cũng đang giả vờ ổn như mình không?” hoặc “Đây là cảm giác của những người lớn lên quá sớm.”`;
    }
    if (lower.includes('7 ngày')) {
        return `Lịch 7 ngày cho ${channelName}: 2 video storytelling, 2 video quote cinematic, 1 video POV học sinh, 1 video healing, 1 video tổng kết tuần. Đăng vào 11:30 hoặc 20:30 để dễ test retention.`;
    }
    if (lower.includes('phân tích') || lower.includes('tình trạng')) {
        return context?.summary
            ? `Tôi đọc dữ liệu gần đây của ${channelName}: ${context.summary}. Gợi ý: giữ format thắng, rút ngắn mở đầu còn 1.5 giây, thêm CTA lưu video thay vì CTA bình luận ở các clip buồn.`
            : `Hiện chưa có đủ dữ liệu sâu, nhưng tôi vẫn gợi ý cho ${channelName}: ưu tiên video giữ cảm xúc rõ ngay 2 giây đầu, đừng đổi style liên tục, và nên lập series 3 video liên tiếp cùng một chủ đề.`;
    }
    if (lower.includes('flop')) {
        return `Khi video flop, đừng sửa tất cả cùng lúc. Hãy test lại 3 thứ theo thứ tự: hook 2 giây đầu, caption ngắn hơn, và thumbnail/frame mở đầu sáng hơn.`;
    }
    return `Với ${channelName}, ưu tiên giữ style nhất quán, làm video ngắn 15-25 giây, 1 ý chính mỗi video, hook thẳng vấn đề, và CTA mềm. ${context?.summary ? `Tóm tắt hiện tại: ${context.summary}.` : ''}`.trim();
}
export function buildStoryboard(prompt) {
    const styles = ['Mở đầu', 'Nhịp giữa', 'Đẩy cảm xúc', 'Kết CTA'];
    return Array.from({ length: 4 }).map((_, index) => ({
        sceneIndex: index + 1,
        title: styles[index],
        prompt: `${prompt}. Cảnh ${index + 1}, ${seeds.scenes[index % seeds.scenes.length]}, ${seeds.camera[index % seeds.camera.length]}.`,
        subtitle: [
            'Có những ngày mình chỉ muốn đi thật chậm...',
            'để nghe rõ điều lòng mình đang nói.',
            'Không cần ai hiểu hết, chỉ cần mình đừng bỏ rơi mình.',
            'Lưu video này nếu bạn cũng từng như vậy.'
        ][index],
        durationMs: [2500, 3000, 3000, 2500][index]
    }));
}
function normalizeTag(input) {
    return input
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^a-zA-Z0-9]+/g, '')
        .toLowerCase() || 'tiktok';
}
